import React, { useEffect, useState } from 'react';
import { useAdminLanguage } from '../AdminLanguageContext';
import api from '../api';
import './Dashboard.css';

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="stat-card">
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
        {sub && <div className="stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

function MiniChart({ data, color = '#4154f1', emptyText }) {
  if (!data || data.length === 0) return <div className="mini-chart-empty">{emptyText}</div>;
  const max = Math.max(...data.map(d => d.cnt), 1);
  return (
    <div className="mini-chart">
      {data.map((d, i) => (
        <div key={i} className="bar-wrap" title={`${d.day}: ${d.cnt}`}>
          <div className="bar" style={{ height: `${(d.cnt / max) * 100}%`, background: color }} />
          <div className="bar-label">{d.day?.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

function RevenueChart({ data, emptyText }) {
  if (!data || data.length === 0) return <div className="mini-chart-empty">{emptyText}</div>;
  const max = Math.max(...data.map(item => Number(item.amount || 0)), 1);
  return (
    <div className="revenue-chart">
      {data.map(item => (
        <div key={item.month} className="revenue-bar-wrap" title={`${item.month}: ¥ ${(Number(item.amount || 0) / 100).toFixed(2)}`}>
          <div className="revenue-bar" style={{ height: `${(Number(item.amount || 0) / max) * 100}%` }} />
          <div className="revenue-bar-label">{item.month.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

function formatYuan(cents) {
  return `¥ ${(Number(cents || 0) / 100).toFixed(2)}`;
}

export default function Dashboard() {
  const { copy } = useAdminLanguage();
  const dashboardCopy = copy.dashboard;
  const legacyUsersLabel = dashboardCopy.stats.users;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/stats').then(res => setStats(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, color: '#6b7280' }}>{dashboardCopy.loading}</div>;
  if (!stats) return null;

  return (
    <div>
      <div className="page-title">{dashboardCopy.title}</div>

      {/* 核心指标 */}
      <div className="stats-grid" data-legacy-users-label={legacyUsersLabel}>
        <StatCard icon="👥" label={dashboardCopy.stats.totalUsers} value={stats.totalUsers ?? stats.users} sub={dashboardCopy.sub.newThisWeek(stats.newUsers7d)} />
        <StatCard icon="✓" label={dashboardCopy.stats.activeSellers} value={stats.activeSellers ?? 0} sub={dashboardCopy.sub.sellers(stats.activeSellers ?? 0)} />
        <StatCard icon="¥" label={dashboardCopy.stats.currentMonthGmv} value={formatYuan(stats.currentMonthGmv)} sub={dashboardCopy.sub.todo(stats.pendingTodos ?? 0)} />
        <StatCard icon="!" label={dashboardCopy.stats.pendingTodos} value={stats.pendingTodos ?? 0} sub={dashboardCopy.sub.todo(stats.pendingTodos ?? 0)} />
        <StatCard icon="📋" label={dashboardCopy.stats.tasks} value={stats.tasks} sub={dashboardCopy.sub.openTasks(stats.openTasks)} />
        <StatCard icon="💬" label={dashboardCopy.stats.posts} value={stats.posts} sub={dashboardCopy.sub.newThisWeek(stats.newPosts7d)} />
        <StatCard icon="⭐" label={dashboardCopy.stats.avgRating} value={`${stats.avgRating}/5`} sub={dashboardCopy.sub.reviews(stats.reviews)} />
        <StatCard icon="💰" label={dashboardCopy.stats.bids} value={stats.bids} />
        <StatCard icon="🔁" label={dashboardCopy.stats.replies} value={stats.replies} />
      </div>

      {/* 趋势图表 */}
      <div className="charts-row">
        <div className="card chart-card">
          <div className="chart-title">{dashboardCopy.charts.users14d}</div>
          <MiniChart data={stats.userTrend} color="#4154f1" emptyText={dashboardCopy.empty} />
        </div>
        <div className="card chart-card">
          <div className="chart-title">{dashboardCopy.charts.tasks14d}</div>
          <MiniChart data={stats.taskTrend} color="#10b981" emptyText={dashboardCopy.empty} />
        </div>
      </div>

      <div className="dashboard-operations-grid">
        <div className="card dashboard-panel">
          <div className="chart-title">{dashboardCopy.charts.revenue12Months}</div>
          <RevenueChart data={stats.revenue12Months} emptyText={dashboardCopy.empty} />
        </div>
        <div className="card dashboard-panel">
          <div className="chart-title">{dashboardCopy.charts.categoryRanking}</div>
          <div className="ranking-list">
            {(stats.categoryRanking || []).map(item => {
              const max = Math.max(...(stats.categoryRanking || []).map(row => Number(row.amount || 0)), 1);
              return (
                <div className="ranking-row" key={item.category}>
                  <div className="ranking-meta">
                    <span>{item.category}</span>
                    <strong>{formatYuan(item.amount)}</strong>
                  </div>
                  <div className="ranking-track">
                    <span style={{ width: `${(Number(item.amount || 0) / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
            {(!stats.categoryRanking || stats.categoryRanking.length === 0) && (
              <div className="mini-chart-empty">{dashboardCopy.empty}</div>
            )}
          </div>
        </div>
      </div>

      <div className="card dashboard-panel dashboard-todo-panel">
        <div className="chart-title">{dashboardCopy.todos.title}</div>
        <div className="todo-list">
          {[
            { key: 'sellerVerification', href: '/sellers', value: stats.todoBreakdown?.sellerVerification },
            { key: 'reportedServices', href: '/reports', value: stats.todoBreakdown?.reportedServices },
            { key: 'refunds', href: '/refunds', value: stats.todoBreakdown?.refunds },
            { key: 'withdrawals', href: '/withdrawals', value: stats.todoBreakdown?.withdrawals },
          ].map(item => (
            <a className="todo-row" href={item.href} key={item.key}>
              <span>{dashboardCopy.todos[item.key]}</span>
              <strong>{Number(item.value || 0)}</strong>
              <span className="todo-view">{dashboardCopy.todos.view} →</span>
            </a>
          ))}
        </div>
      </div>

      <div className="dashboard-operations-grid">
        <div className="card dashboard-panel">
          <div className="chart-title">{dashboardCopy.charts.topSellers}</div>
          <div className="dashboard-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{copy.sellers.columns.seller}</th>
                  <th>{copy.sellers.columns.orders}</th>
                  <th>{copy.sellers.columns.settlement}</th>
                </tr>
              </thead>
              <tbody>
                {(stats.topSellers || []).map((seller, index) => (
                  <tr key={seller.id}>
                    <td className="rank-number">{index + 1}</td>
                    <td>{seller.username}</td>
                    <td>{seller.completedOrders}</td>
                    <td className="seller-money">{formatYuan(seller.sellerSettlement)}</td>
                  </tr>
                ))}
                {(!stats.topSellers || stats.topSellers.length === 0) && (
                  <tr><td colSpan={4} className="seller-empty">{dashboardCopy.empty}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card dashboard-panel">
          <div className="chart-title">{dashboardCopy.charts.recentOrders}</div>
          <div className="dashboard-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{copy.orders.columns.orderNo}</th>
                  <th>{copy.orders.columns.seller}</th>
                  <th>{copy.orders.columns.amount}</th>
                  <th>{copy.orders.columns.status}</th>
                </tr>
              </thead>
              <tbody>
                {(stats.recentOrders || []).map(order => (
                  <tr key={order.id}>
                    <td>{order.order_no || `#${order.id}`}</td>
                    <td>{order.seller_name || copy.users.emptyValue}</td>
                    <td className="seller-money">{formatYuan(order.amount)}</td>
                    <td>{copy.orders.status[order.status] || order.status}</td>
                  </tr>
                ))}
                {(!stats.recentOrders || stats.recentOrders.length === 0) && (
                  <tr><td colSpan={4} className="seller-empty">{dashboardCopy.empty}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 快捷操作 */}
      <div className="card" style={{ marginTop: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{dashboardCopy.quickActionsTitle}</div>
        <div className="quick-actions">
          {[
            { label: dashboardCopy.quickActions.users, href: '/users', icon: '👥', color: '#4154f1' },
            { label: dashboardCopy.quickActions.sellers, href: '/sellers', icon: '✓', color: '#0f7f5c' },
            { label: dashboardCopy.quickActions.tasks, href: '/tasks', icon: '📋', color: '#10b981' },
            { label: copy.layout.nav.orders, href: '/orders', icon: '▣', color: '#2563eb' },
            { label: copy.layout.nav.withdrawals, href: '/withdrawals', icon: '$', color: '#d97706' },
            { label: dashboardCopy.quickActions.posts, href: '/posts', icon: '💬', color: '#f59e0b' },
            { label: dashboardCopy.quickActions.announcements, href: '/announcements', icon: '📢', color: '#8b5cf6' },
            { label: dashboardCopy.quickActions.categories, href: '/categories', icon: '🗂️', color: '#ef4444' },
          ].map(a => (
            <a key={a.href} href={a.href} className="quick-action-btn" style={{ borderColor: a.color, color: a.color }}>
              <span style={{ fontSize: 20 }}>{a.icon}</span>
              <span>{a.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
